import React, { useState, useRef, useCallback, useMemo } from 'react';
import cx from 'classnames';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import { NO_RESPONSE } from '../../constants/constants';
import useFormativeScreen from '../Formative/useFormativeScreen';
import { useContainerHeightVar } from '../Formative/containerHeight';
import SequencingPromptBar from './SequencingPromptBar';
import SequencingMediaSection from './SequencingMediaSection';
import SequencingTrack from './SequencingTrack';
import SequencingPool from './SequencingPool';
import SequencingUndoButton from './SequencingUndoButton';
import SequencingSubmitButton from './SequencingSubmitButton';
import { LOG, encodePlacements, trackZones } from './sequencingConstants';

const EMPTY_PLACEMENTS = [];

const Sequencing = ({
  speech,
  options,
  dropZones,
  videoURL,
  imageURL,
  backgroundImageUrl,
  readOptionsAloud,
  isInstruction,
  isRepeating,
  onDone,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  idleTimeoutOverrides,
  idleEncouragementText,
  instructionRepeatable,
  instructionMaxRepeats,
  instructionRepeatVariation,
  repeatInstructions,
  handleRepeatInstructions,
  beginOverlayRepeatPlayback,
  endOverlayRepeatPlayback,
  setHideParentRepeatButton,
  currentTriggerAction,
  currentTriggerData,
  onTriggerActionComplete,
  deferPoolNarration,
  verbalInstructions,
}) => {
  const pool = useMemo(() => options || [], [options]);
  const track = useMemo(() => dropZones || [], [dropZones]);
  const zones = useMemo(() => trackZones(dropZones), [dropZones]);

  // The track renders every entry and has to place each zone by its ordinal. Resolving that
  // with findIndex per entry rescans zones on every render; first-wins matches findIndex.
  const zoneIndexByKey = useMemo(() => {
    const byKey = new Map();
    zones.forEach((zone, index) => {
      if (!byKey.has(zone.key)) byKey.set(zone.key, index);
    });
    return byKey;
  }, [zones]);

  const poolByKey = useMemo(() => {
    const byKey = {};
    for (const poolItem of pool) byKey[poolItem.key] = poolItem;
    return byKey;
  }, [pool]);

  const [placements, setPlacements] = useState(EMPTY_PLACEMENTS);

  const isMountedRef = useIsMounted();
  const containerRef = useRef(null);
  useContainerHeightVar(containerRef, '--seq-h');

  const placementsRef = useRef(EMPTY_PLACEMENTS);
  placementsRef.current = placements;

  const placedKeys = useMemo(() => new Set(placements), [placements]);

  const trackIsDirty = placements.length > 0;
  const allZonesFilled = zones.length > 0 && placements.length >= zones.length;

  const clearTrack = useCallback(() => setPlacements(EMPTY_PLACEMENTS), []);

  const encodeResponse = useCallback(() => {
    const placed = placementsRef.current;
    return placed.length > 0 ? encodePlacements(zones, placed) : NO_RESPONSE;
  }, [zones]);

  const screen = useFormativeScreen({
    name: 'sequencing',
    log: LOG,
    speech,
    pool,
    hasImage: !!imageURL,
    hasVideo: !!videoURL,
    readOptionsAloud,
    isInstruction,
    isRepeating,
    shouldExtendTimeout,
    timeoutExtensionMultiplier,
    idleTimeoutOverrides,
    idleEncouragementText,
    instructionRepeatable,
    instructionMaxRepeats,
    instructionRepeatVariation,
    repeatInstructions,
    handleRepeatInstructions,
    beginOverlayRepeatPlayback,
    endOverlayRepeatPlayback,
    setHideParentRepeatButton,
    currentTriggerAction,
    currentTriggerData,
    onTriggerActionComplete,
    deferPoolNarration,
    verbalInstructions,
    onClearBoard: clearTrack,
    encodeResponse,
    onDone,
  });

  const {
    hidden,
    layout,
    boardDisabled: trackDisabled,
    pointerTarget,
    encouragementText,
    resetIdleTimer,
    clearIdleTimer,
    narratePoolItem,
  } = screen;

  const narratePoolItemRef = useRef(narratePoolItem);
  narratePoolItemRef.current = narratePoolItem;
  const submittedRef = useRef(screen.submitted);
  submittedRef.current = screen.submitted;
  const selectionGenerationRef = useRef(0);

  const handlePoolItemClick = useCallback(
    async (poolItem) => {
      if (trackDisabled) return;
      if (placementsRef.current.includes(poolItem.key)) return;
      if (placementsRef.current.length >= zones.length) return;

      selectionGenerationRef.current += 1;
      const generation = selectionGenerationRef.current;
      speech.abortSpeech?.();
      setPlacements((previous) => {
        if (previous.includes(poolItem.key)) return previous;
        if (previous.length >= zones.length) return previous;
        return [...previous, poolItem.key];
      });
      clearIdleTimer();
      await narratePoolItemRef.current(poolItem);
      if (!isMountedRef.current || submittedRef.current) return;
      // A second tap aborts this narration, which resolves the await above rather than
      // rejecting it. Restarting the idle timer from here would do it while the tap that
      // superseded us is still speaking.
      if (selectionGenerationRef.current !== generation) return;
      resetIdleTimer(encouragementText);
    },
    [
      trackDisabled,
      zones.length,
      speech,
      clearIdleTimer,
      resetIdleTimer,
      encouragementText,
      isMountedRef,
    ]
  );

  const handleUndo = useCallback(() => {
    if (trackDisabled) return;
    if (placementsRef.current.length === 0) return;
    speech.abortSpeech?.();
    setPlacements((previous) => previous.slice(0, -1));
    resetIdleTimer(encouragementText);
  }, [trackDisabled, speech, resetIdleTimer, encouragementText]);

  return (
    <div
      ref={containerRef}
      className={cx('sequencing', {
        'sequencing--imageOnly': layout.imageOnly,
        'sequencing--videoOnly': layout.videoOnly,
        'sequencing--audioOnly': layout.audioOnly,
        'sequencing--noStimulus': !videoURL && !imageURL,
      })}
    >
      <SequencingPromptBar
        hidePrompt={hidden.prompt}
        hideRepeat={hidden.repeat}
        onRepeatClick={screen.onRepeatClick}
        repeatDisabled={screen.repeatDisabled}
        showRepeatPointer={pointerTarget === 'repeat'}
      />

      <SequencingMediaSection
        hasVideo={!!videoURL}
        hasImage={!!imageURL}
        hideMedia={hidden.media}
        videoURL={videoURL}
        imageURL={imageURL}
        speech={speech}
        videoPlaying={screen.videoPlaying}
        maxVideoReplays={screen.maxVideoReplays}
        onVideoReplay={screen.consumeStimulusReplay}
        unmountVideoWhenDone={screen.unmountVideoWhenDone}
        suppressVideoReplayOverlay={screen.suppressVideoReplayOverlay}
        showRepeatOverlay={screen.showRepeatOverlay}
        showRepeatOverlayReplay={screen.showRepeatOverlayReplay}
        pointerTarget={pointerTarget}
        onRepeatOverlayReplay={screen.handleRepeatOverlayReplay}
        onRepeatOverlayNext={screen.handleRepeatOverlayNext}
        onVideoStart={screen.onVideoStart}
        onVideoDone={screen.onVideoDone}
      />

      <div className="sequencing__workspace">
        <SequencingTrack
          track={track}
          zones={zones}
          zoneIndexByKey={zoneIndexByKey}
          placements={placements}
          poolByKey={poolByKey}
          hideTrack={hidden.board}
          pointerTarget={pointerTarget}
          backgroundImageUrl={backgroundImageUrl}
        />

        <SequencingPool
          pool={pool}
          placedKeys={placedKeys}
          revealedKeys={screen.revealedKeys}
          playingPoolKey={screen.playingPoolKey}
          narrationPassActive={screen.narrationPassActive}
          hidePool={hidden.pool}
          pointerTarget={pointerTarget}
          disabled={trackDisabled}
          onPoolItemClick={handlePoolItemClick}
        />
      </div>

      <div className="sequencing__cta">
        <SequencingUndoButton
          hideUndo={hidden.undo}
          pointerTarget={pointerTarget}
          undoEnabled={trackIsDirty && !trackDisabled}
          onUndo={handleUndo}
        />
        <SequencingSubmitButton
          hideSubmit={hidden.submit}
          pointerTarget={pointerTarget}
          isInstruction={isInstruction}
          isRepeating={isRepeating}
          allPositionsFilled={allZonesFilled}
          submitEnabled={allZonesFilled && !trackDisabled}
          onSubmit={screen.handleSubmit}
        />
      </div>
    </div>
  );
};

export default Sequencing;
